import React, { useState } from 'react';
import SessionTopBar from './SessionTopBar';
import SessionSidebar, { SidebarView } from './SessionSidebar';

interface Phase {
  id: string;
  name: string;
}

interface SessionLayoutProps {
  // Top bar props
  sessionId: string;
  sessionName: string;
  phases: Phase[];
  currentPhaseIndex: number;
  onPhaseChange: (index: number) => void;
  isConnected: boolean;
  playbackMode: 'browser' | 'physical' | 'both';
  onPlaybackModeChange: (mode: 'browser' | 'physical' | 'both') => void;
  onShareSession: () => void;
  onEndSession: () => void;
  sessionMode: 'active' | 'readOnly';
  projectId?: string;
  projectName?: string;

  // Sidebar props
  pendingContributionsCount: number;
  pendingDecisionsCount: number;
  pendingActionsCount?: number;
  avatarCount: number;

  // View state
  activeView: SidebarView;
  onViewChange: (view: SidebarView) => void;

  // Status banner
  sessionStatus: 'active' | 'completed' | 'abandoned' | null;
  userAccessLevel: 'owner' | 'read' | 'write';
  onReopenSession?: () => void;

  // Second screen
  isSecondScreenOpen?: boolean;
  onOpenSecondScreen?: () => void;

  // Rename
  onRename?: (newName: string) => void;

  // Feature flags
  hasVexaFeature?: boolean;
  hasMiroFeature?: boolean;
  hasReachFeature?: boolean;

  // Room mic
  roomMicMuted?: boolean;
  onToggleRoomMic?: () => void;

  // Data audit
  onAuditData?: () => void;

  // Content
  children: React.ReactNode;
}

const SessionLayout: React.FC<SessionLayoutProps> = ({
  sessionId,
  sessionName,
  phases,
  currentPhaseIndex,
  onPhaseChange,
  isConnected,
  playbackMode,
  onPlaybackModeChange,
  onShareSession,
  onEndSession,
  sessionMode,
  projectId,
  projectName,
  pendingContributionsCount,
  pendingDecisionsCount,
  pendingActionsCount,
  avatarCount,
  activeView,
  onViewChange,
  sessionStatus,
  userAccessLevel,
  isSecondScreenOpen,
  onOpenSecondScreen,
  onRename,
  hasVexaFeature,
  hasMiroFeature,
  hasReachFeature,
  roomMicMuted,
  onToggleRoomMic,
  onReopenSession,
  onAuditData,
  children,
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const showStatusBanner = sessionMode === 'readOnly' || userAccessLevel !== 'owner';

  return (
    <div className="font-display bg-surface-light text-slate-900 overflow-hidden h-screen flex flex-col">
      {/* Top Bar */}
      <SessionTopBar
        sessionId={sessionId}
        sessionName={sessionName}
        phases={phases}
        currentPhaseIndex={currentPhaseIndex}
        onPhaseChange={onPhaseChange}
        isConnected={isConnected}
        playbackMode={playbackMode}
        onPlaybackModeChange={onPlaybackModeChange}
        onShareSession={onShareSession}
        onEndSession={onEndSession}
        sessionMode={sessionMode}
        projectId={projectId}
        projectName={projectName}
        isSecondScreenOpen={isSecondScreenOpen}
        onOpenSecondScreen={onOpenSecondScreen}
        onRename={onRename}
      />

      {/* Status Banner */}
      {showStatusBanner && (
        <div
          className={`px-6 py-2 flex items-center justify-between text-sm ${
            sessionStatus === 'completed'
              ? 'bg-green-50 text-green-800 border-b border-green-200'
              : sessionStatus === 'abandoned'
              ? 'bg-amber-50 text-amber-800 border-b border-amber-200'
              : 'bg-blue-50 text-blue-800 border-b border-blue-200'
          }`}
        >
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>
              {sessionMode === 'readOnly' && 'Read-only mode'}
              {sessionStatus === 'completed' && ' — Session completed'}
              {sessionStatus === 'abandoned' && ' — Session abandoned'}
              {userAccessLevel === 'read' && ' — You have read-only access'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {onReopenSession && sessionStatus === 'completed' && (
              <button
                onClick={onReopenSession}
                className="text-xs font-medium px-3 py-1 rounded bg-green-700 text-white hover:bg-green-800"
              >
                Reopen Session
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Body: Sidebar + Content + Chat */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <SessionSidebar
          activeView={activeView}
          onViewChange={onViewChange}
          pendingContributionsCount={pendingContributionsCount}
          pendingDecisionsCount={pendingDecisionsCount}
          pendingActionsCount={pendingActionsCount}
          avatarCount={avatarCount}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          hasVexaFeature={hasVexaFeature}
          hasMiroFeature={hasMiroFeature}
          hasReachFeature={hasReachFeature}
          roomMicMuted={roomMicMuted}
          onToggleRoomMic={onToggleRoomMic}
          sessionId={sessionId}
          onAuditData={onAuditData}
        />

        {/* Main Content Area */}
        <div className="flex-1 min-w-0 flex overflow-hidden">
          <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-y-auto overflow-x-hidden">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionLayout;
